/**
 * My Account wishlist — Wysh-backed rows matching Figma (image, size, qty, ATC, share).
 * Markup comes from the <template data-fv-wishlist-*> elements in account-panel-wishlist.liquid.
 */
import {
  WYSH_CACHE_KEY,
  addToWysh,
  defaultWishlistKey,
  proxyPath,
  readWyshWishlists,
  removeFromWysh,
} from '@theme/fluevog-wishlist';
import {
  addVariantToCart,
  displayTitle,
  familyName,
  fetchWishlistProducts,
  numericId,
  pickVariant,
  readWishlistEntries,
} from '@theme/fluevog-wishlist-products';

/** One option per distinct size, in variant order. */
function sizeChoices(variants) {
  const seen = new Set();
  return variants.flatMap((variant) => {
    const size = (variant.selectedOptions || []).find((option) =>
      (option.name || '').toLowerCase().includes('size')
    )?.value;
    if (!size || size === 'Default Title' || seen.has(size)) return [];
    seen.add(size);
    return [{ label: size, variantId: numericId(variant.id), available: Boolean(variant.availableForSale) }];
  });
}

function shareUrls(product, imageUrl) {
  const url = encodeURIComponent(`${window.location.origin}/products/${product.handle}`);
  const title = encodeURIComponent(displayTitle(product.title));
  const image = encodeURIComponent(imageUrl || '');
  return {
    email: `mailto:?subject=${title}&body=${url}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${url}&media=${image}&description=${title}`,
    twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
  };
}

class FluevogAccountWishlist extends HTMLElement {
  connectedCallback() {
    this._openListKeys = new Set();
    this._onStorage = (event) => {
      if (!event.key || event.key === WYSH_CACHE_KEY) this.render();
    };
    window.addEventListener('storage', this._onStorage);
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener('storage', this._onStorage);
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
  }

  _onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const qtyBtn = target.closest('[data-fv-qty]');
    if (qtyBtn) {
      event.preventDefault();
      this._adjustQty(qtyBtn);
      return;
    }

    const addBtn = target.closest('[data-fv-add-to-cart]');
    if (addBtn) {
      event.preventDefault();
      this._addToCart(addBtn);
      return;
    }

    const removeBtn = target.closest('[data-fv-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      this._removeItem(removeBtn);
    }
  };

  _onChange = async (event) => {
    const select = event.target instanceof HTMLSelectElement ? event.target : null;
    if (!select?.matches('[data-fv-size-select]')) return;
    const row = select.closest('[data-fv-wishlist-row]');
    if (!row) return;

    const addBtn = row.querySelector('[data-fv-add-to-cart]');
    const removeBtn = row.querySelector('[data-fv-wishlist-remove]');
    const productId = row.dataset.productId || '';
    const variantId = select.value;
    const previousVariantId =
      select.dataset.previousVariantId || removeBtn?.dataset.variantId || '';

    if (addBtn) {
      addBtn.dataset.variantId = variantId;
      addBtn.disabled = !variantId;
    }

    if (!productId || !variantId || variantId === previousVariantId) return;

    select.disabled = true;
    try {
      const wishlistKey = row.dataset.wishlistKey || defaultWishlistKey();
      if (!wishlistKey) throw new Error('No wishlist key found');

      // Add the new size first so the item never briefly disappears from Wysh.
      await addToWysh(productId, variantId, wishlistKey);
      if (previousVariantId) {
        await removeFromWysh(productId, previousVariantId, wishlistKey);
      }

      if (removeBtn) removeBtn.dataset.variantId = variantId;
      select.dataset.previousVariantId = variantId;
    } catch (error) {
      console.error(error);
      select.value = previousVariantId;
      if (addBtn) {
        addBtn.dataset.variantId = previousVariantId;
        addBtn.disabled = !previousVariantId;
      }
    } finally {
      select.disabled = false;
    }
  };

  _adjustQty(button) {
    const input = button.closest('[data-fv-wishlist-row]')?.querySelector('[data-fv-qty-value]');
    if (!input) return;
    const next = Math.max(1, (Number(input.textContent) || 1) + (Number(button.dataset.fvQty) || 0));
    input.textContent = String(next);
  }

  async _addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const row = button.closest('[data-fv-wishlist-row]');
    const quantity = Number(row?.querySelector('[data-fv-qty-value]')?.textContent) || 1;
    button.disabled = true;
    try {
      await addVariantToCart(variantId, quantity, 'fv-account-wishlist');
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
    }
  }

  async _removeItem(button) {
    const productId = button.dataset.productId;
    if (!productId || button.disabled) return;

    // Remove from the list this row belongs to, not just the first list.
    const row = button.closest('[data-fv-wishlist-row]');
    button.disabled = true;
    try {
      await removeFromWysh(
        productId,
        button.dataset.variantId || '',
        row?.dataset.wishlistKey || defaultWishlistKey()
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.render();
    }
  }

  /** First element of a sibling <template data-fv-wishlist-{name}>. */
  _cloneTemplate(name) {
    const template = this.parentElement?.querySelector(`template[data-fv-wishlist-${name}]`);
    return template?.content.cloneNode(true).firstElementChild ?? null;
  }

  /** “[[count]] items” / “You have [[count]] wishlists” from the data-*-one/other-label copy. */
  _countLabel(count, key) {
    const template = this.dataset[`${key}${count === 1 ? 'One' : 'Other'}Label`] || '';
    return template.replace('[[count]]', String(count));
  }

  _setEmpty(isEmpty) {
    this.classList.toggle('is-empty', isEmpty);
    this.closest('.fv-account-wishlist__box')?.classList.toggle('is-empty', isEmpty);
  }

  /** One saved product: image, size picker, quantity, add to cart, share links. */
  _renderRow(entry, product, families) {
    const { variant, sizeSelected, variants } = pickVariant(product, entry.variantIds);
    // Only preselect a size when Wysh stored a variant — don't invent one.
    const variantId = sizeSelected ? numericId(variant.id) : '';
    const image = variant?.image?.url || product.featuredImage?.url || '';
    const title = displayTitle(product.title);
    const family = familyName(product, families);
    const href = `/products/${product.handle}${variantId ? `?variant=${variantId}` : ''}`;

    const row = this._cloneTemplate('row');
    row.dataset.productId = entry.productId;
    row.dataset.wishlistKey = entry.wishlistKey || '';

    const remove = row.querySelector('[data-fv-wishlist-remove]');
    remove.dataset.productId = entry.productId;
    remove.dataset.variantId = variantId;
    remove.setAttribute('aria-label', `${remove.getAttribute('aria-label')} ${title}`);

    row.querySelectorAll('[data-fv-row-link]').forEach((link) => link.setAttribute('href', href));
    row.querySelector('[data-fv-row-title]').textContent = title;

    const img = row.querySelector('[data-fv-row-image]');
    if (image) Object.assign(img, { src: image, alt: title });
    else img.remove();

    const familyEl = row.querySelector('[data-fv-row-family]');
    if (family) familyEl.textContent = family;
    else familyEl.remove();

    const select = row.querySelector('[data-fv-size-select]');
    select.dataset.previousVariantId = variantId;
    const choices = sizeChoices(variants);
    if (choices.length) {
      if (!sizeSelected) select.add(Object.assign(new Option('Select', '', true, true), { disabled: true }));
      choices.forEach((choice) => {
        const selected = choice.variantId === variantId;
        select.add(Object.assign(new Option(choice.label, choice.variantId, selected, selected), {
          disabled: !choice.available,
        }));
      });
    } else {
      select.add(new Option('—', variantId, true, true));
      select.disabled = true;
    }

    const addBtn = row.querySelector('[data-fv-add-to-cart]');
    addBtn.dataset.variantId = variantId;
    addBtn.disabled = !variantId;

    const shares = shareUrls(product, image);
    row.querySelectorAll('[data-fv-share]').forEach((link) => link.setAttribute('href', shares[link.dataset.fvShare]));

    return row;
  }

  /** One wishlist as an accordion: name, count and “View list” in the header. */
  _renderGroup({ list, rows }) {
    const key = String(list.wishlist_key);
    const group = this._cloneTemplate('group');
    group.dataset.wishlistKey = key;
    group.open = this._openListKeys.has(key);
    group.querySelector('[data-fv-group-title]').textContent =
      list.name || this.dataset.defaultListName || 'My Wishlist';
    group.querySelector('[data-fv-group-count]').textContent = this._countLabel(rows.length, 'items');
    group.querySelector('[data-fv-group-link]').setAttribute('href', proxyPath(`/${key}`));
    group.querySelector('[data-fv-group-list]').append(...rows);
    return group;
  }

  renderEmpty() {
    this._setEmpty(true);
    this.replaceChildren(this._cloneTemplate('empty'));
  }

  async render() {
    // Keep expanded lists expanded across re-renders (remove, size change, cache sync).
    this._openListKeys = new Set(
      [...this.querySelectorAll('.fv-account-wishlist__group[open]')].map((group) => group.dataset.wishlistKey)
    );

    const loading = document.createElement('p');
    loading.className = 'fv-account-wishlist__loading';
    loading.textContent = this.dataset.loadingMessage || 'Loading wishlist…';
    this.replaceChildren(loading);

    const wishlists = readWyshWishlists();
    const entries = readWishlistEntries(wishlists);
    if (!entries.length) return this.renderEmpty();

    try {
      const { productsById, families } = await fetchWishlistProducts(entries);
      const groups = wishlists.map((list, listIndex) => ({
        list,
        rows: entries
          .filter((entry) => entry.listIndex === listIndex && productsById.has(entry.productId))
          .map((entry) => this._renderRow(entry, productsById.get(entry.productId), families)),
      }));
      if (!groups.some((group) => group.rows.length)) return this.renderEmpty();

      this._setEmpty(false);

      // One list: plain rows. Several: say how many, and group rows under each list.
      const keyedGroups = groups.filter((group) => group.list?.wishlist_key);
      if (keyedGroups.length > 1) {
        const summary = this._cloneTemplate('summary');
        summary.textContent = this._countLabel(keyedGroups.length, 'lists');
        this.replaceChildren(summary, ...keyedGroups.map((group) => this._renderGroup(group)));
      } else {
        const list = document.createElement('div');
        list.className = 'fv-account-wishlist__list';
        list.append(...groups.flatMap((group) => group.rows));
        this.replaceChildren(list);
      }
    } catch (error) {
      console.error(error);
      this.renderEmpty();
    }
  }
}

if (!customElements.get('fluevog-account-wishlist')) {
  customElements.define('fluevog-account-wishlist', FluevogAccountWishlist);
}
